'use client';

import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import {
  ValuationInput,
  PlayerSaleInput,
  ValuationCalculation,
  MediaEntry,
  MediaEntryInput,
} from '@/lib/platform/types';
import {
  calculateCompleteValuation,
  buildSummaryFromEntries,
} from '@/lib/platform/utils/calculations';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { formatCurrency } from '@/lib/platform/utils/currency';
import { useLanguage } from '@/lib/platform/contexts/language-context';
import { getTranslation } from '@/lib/platform/data/translations';
import { useOrganizationLocale } from '@/lib/platform/hooks/use-organization-locale';
import { useAlias } from '@/lib/platform/hooks/use-alias';
import {
  useAiFormBridge,
  type AiFormFieldDescriptor,
} from '@/lib/platform/contexts/ai-form-bridge-context';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
import { usePluginSettingsQuery } from '@/lib/platform/hooks/queries/use-plugin-settings-query';
import { useMediaEntriesQuery } from '@/lib/platform/hooks/queries/use-media-entries-query';
import { usePlayerSalesVisibility } from '@/lib/platform/hooks/use-player-sales-visibility';
import { logger } from '@/lib/platform/utils/logger';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import SlideOverPanel from '@/components/platform/ui/SlideOverPanel';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useRouter } from 'next/navigation';
import { PrimaryButton, SecondaryButton, DangerButton } from '@/components/platform/buttons';
import { ConfirmModal } from '@/components/platform/modals';
import {
  ValuationDateSection,
  SalesValueSection,
  PlayerSalesSection,
  MediaValueHeaderSection,
  MediaEntrySection,
  SocialMediaSection,
  MatchHospitalitySection,
  BrandValueSection,
} from '@/components/products/valuations/valuation-sections';
import { useDemoMode } from '@/lib/platform/hooks/use-demo-mode';
import { UpgradeRequiredModal } from '@/components/platform/demo-mode/UpgradeRequiredModal';
import { StatusChip } from '@/components/platform/ui/StatusChip';
import type { PlatformMetrics } from '@/lib/products/valuations/social-deepdive/types';

/** The channels that use multi-line entries in the form (plan 5.5, D5 revision). */
type FormMediaChannel = 'tv' | 'webtv' | 'pr' | 'digital' | 'influencer';

const FORM_MEDIA_CHANNELS: FormMediaChannel[] = ['tv', 'webtv', 'pr', 'digital', 'influencer'];

/** Summary fields to zero when a channel has no entry lines left. */
const MEDIA_CHANNEL_SUMMARY_ZERO: Record<FormMediaChannel, Partial<ValuationInput>> = {
  tv: { tv_duration_min: 0, tv_size_category: 'M', tv_cpm: 0, tv_viewers: 0 },
  webtv: { webtv_views: 0, webtv_cpm: 0 },
  pr: { pr_reach: 0, pr_cpm: 0 },
  digital: { digital_impressions: 0, digital_cpm: 0 },
  influencer: {
    influencer_reach: 0,
    influencer_reach_cpm: 0,
    influencer_impressions: 0,
    influencer_impressions_cpm: 0,
    influencer_engagements: 0,
    influencer_engagements_cpm: 0,
  },
};

function blankMediaEntry(channel: FormMediaChannel): MediaEntryInput {
  return {
    channel,
    name: '',
    sort_order: 0,
    volume: 0,
    cpm: 0,
    duration_min: channel === 'tv' ? 0 : null,
    size_category: channel === 'tv' ? 'M' : null,
    impressions: channel === 'influencer' ? 0 : null,
    impressions_cpm: channel === 'influencer' ? 0 : null,
    engagements: channel === 'influencer' ? 0 : null,
    engagements_cpm: channel === 'influencer' ? 0 : null,
  };
}

/** True when the line has a name or any non-zero numeric value. */
function mediaEntryHasData(entry: MediaEntryInput): boolean {
  const num = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return (
    Boolean(entry.name?.trim()) ||
    num(entry.volume) !== 0 ||
    num(entry.cpm) !== 0 ||
    num(entry.duration_min) !== 0 ||
    num(entry.impressions) !== 0 ||
    num(entry.impressions_cpm) !== 0 ||
    num(entry.engagements) !== 0 ||
    num(entry.engagements_cpm) !== 0
  );
}

/**
 * Legacy valuations (summary values, no entry lines): render the summary
 * values as line 1 with an empty name (plan 5.5). Channels with all-zero
 * summary values start blank (no line).
 */
function seedMediaEntriesFromSummary(data: ValuationInput): MediaEntryInput[] {
  const entries: MediaEntryInput[] = [];

  if (data.tv_duration_min || data.tv_cpm || data.tv_viewers) {
    entries.push({
      ...blankMediaEntry('tv'),
      volume: data.tv_viewers || 0,
      cpm: data.tv_cpm || 0,
      duration_min: data.tv_duration_min || 0,
      size_category: (data.tv_size_category as 'S' | 'M' | 'L' | 'XL') || 'M',
    });
  }
  if (data.webtv_views || data.webtv_cpm) {
    entries.push({
      ...blankMediaEntry('webtv'),
      volume: data.webtv_views || 0,
      cpm: data.webtv_cpm || 0,
    });
  }
  if (data.pr_reach || data.pr_cpm) {
    entries.push({ ...blankMediaEntry('pr'), volume: data.pr_reach || 0, cpm: data.pr_cpm || 0 });
  }
  if (data.digital_impressions || data.digital_cpm) {
    entries.push({
      ...blankMediaEntry('digital'),
      volume: data.digital_impressions || 0,
      cpm: data.digital_cpm || 0,
    });
  }
  if (
    data.influencer_reach ||
    data.influencer_reach_cpm ||
    data.influencer_impressions ||
    data.influencer_impressions_cpm ||
    data.influencer_engagements ||
    data.influencer_engagements_cpm
  ) {
    entries.push({
      ...blankMediaEntry('influencer'),
      volume: data.influencer_reach || 0,
      cpm: data.influencer_reach_cpm || 0,
      impressions: data.influencer_impressions || 0,
      impressions_cpm: data.influencer_impressions_cpm || 0,
      engagements: data.influencer_engagements || 0,
      engagements_cpm: data.influencer_engagements_cpm || 0,
    });
  }

  return entries;
}

interface ValuationFormProps {
  mode: 'create' | 'edit';
  initialData?: ValuationInput;
  valuationId?: string; // For EDIT mode - to fetch platform data
  clubName: string;
  organizationId: string;
  onSubmit: (
    data: ValuationInput,
    calculation: ValuationCalculation,
    status: 'draft' | 'approved',
    mediaEntries: MediaEntryInput[]
  ) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
  permissions?: {
    canEditSales?: boolean;
    canEditTransfer?: boolean;
    canEditMarketing?: boolean;
    canEditBrand?: boolean;
    canApprove?: boolean;
  };
}

function ValuationForm({
  mode,
  initialData,
  valuationId,
  clubName,
  organizationId,
  onSubmit,
  onDelete,
  permissions,
}: ValuationFormProps) {
  const router = useRouter();
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const { locale } = useLanguage();
  const { settings: localeSettings } = useOrganizationLocale();
  const { getLabel } = useAlias();
  const translations = getTranslation(locale);
  const t = translations.valuationForm;
  const common = translations.common;

  const { data: permissionsData } = usePermissionsQuery();
  const { isPluginEnabled, isLoading: pluginSettingsLoading } =
    usePluginSettingsQuery(organizationId);
  const { isVisible: canViewTransfer } = usePlayerSalesVisibility();

  const canViewSales = permissionsData?.canViewSales ?? false;
  const canViewMarketing = permissionsData?.canViewMarketing ?? false;
  const canViewBrand = permissionsData?.canViewBrand ?? false;
  const { isDemo, blockIfDemo, showUpgradeModal, setShowUpgradeModal } = useDemoMode();
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname.includes('dev.valiyou') ||
      window.location.hostname === 'dev.valiyou.com');
  // Show plugin button by default while loading to avoid flickering, only switch to manual if plugin is confirmed disabled
  const isSocialDeepDiveEnabled = pluginSettingsLoading ? true : isPluginEnabled('social-deepdive');

  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState<'draft' | 'approve' | null>(null);
  const [error, setError] = useState('');
  const [showCalculationDrawer, setShowCalculationDrawer] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showDeletePlayerSaleModal, setShowDeletePlayerSaleModal] = useState(false);
  const [playerSaleToDelete, setPlayerSaleToDelete] = useState<number | null>(null);
  const [mediaEntryToDelete, setMediaEntryToDelete] = useState<{
    channel: FormMediaChannel;
    index: number;
  } | null>(null);
  const [showDeleteValuationModal, setShowDeleteValuationModal] = useState(false);
  const [navigatingBack, setNavigatingBack] = useState(false);
  const [navigatingCancel, setNavigatingCancel] = useState(false);
  const [platformMetrics, setPlatformMetrics] = useState<Record<string, PlatformMetrics>>({});

  // Detect mobile on mount and resize (debounced)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();

    // Debounce resize listener to improve performance
    let timeoutId: NodeJS.Timeout;
    const debouncedCheckMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 150);
    };

    window.addEventListener('resize', debouncedCheckMobile);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedCheckMobile);
    };
  }, []);

  // Permission checks - default to true if not provided (for backwards compatibility)
  const canEditSales = permissions?.canEditSales ?? true;
  const canEditTransfer = permissions?.canEditTransfer ?? true;
  const canEditMarketing = permissions?.canEditMarketing ?? true;
  const canEditBrand = permissions?.canEditBrand ?? true;

  // Test data examples from CSV
  const testDataExamples = {
    football: {
      name: 'High-Profile Football Club',
      merch_revenue: 75000,
      matchday_revenue: 100000,
      player_sales: [
        { player_name: 'Star Player 1', sale_value: 100000 },
        { player_name: 'Star Player 2', sale_value: 50000 },
      ],
      tv_duration_min: 45,
      tv_size_category: 'L' as const,
      tv_cpm: 30,
      tv_viewers: 200000,
      webtv_views: 500000,
      webtv_cpm: 10,
      pr_reach: 1000000,
      pr_cpm: 6,
      digital_impressions: 2000000,
      digital_cpm: 4,
      social_followers: 500000,
      social_followers_cpm: 10,
      social_impressions: 1500000,
      social_impressions_cpm: 8,
      social_engagements: 100000,
      social_cpm: 8,
      influencer_reach: 800000,
      influencer_reach_cpm: 5,
      influencer_impressions: 1200000,
      influencer_impressions_cpm: 6,
      influencer_engagements: 80000,
      influencer_engagements_cpm: 10,
      attendance: 75000,
      attendance_cpm: 15,
      hospitality_guests: 300,
      hospitality_value_per_guest: 600,
      brand_popularity: 9,
      brand_csr: 7,
      brand_culture: 8,
      brand_loyalty: 9,
    },
    basketball: {
      name: 'Regional Basketball Team',
      merch_revenue: 25000,
      matchday_revenue: 40000,
      player_sales: [{ player_name: 'Key Player', sale_value: 30000 }],
      tv_duration_min: 20,
      tv_size_category: 'M' as const,
      tv_cpm: 18,
      tv_viewers: 50000,
      webtv_views: 100000,
      webtv_cpm: 6,
      pr_reach: 200000,
      pr_cpm: 4,
      digital_impressions: 500000,
      digital_cpm: 3,
      social_followers: 100000,
      social_followers_cpm: 6,
      social_impressions: 300000,
      social_impressions_cpm: 5,
      social_engagements: 25000,
      social_cpm: 5,
      influencer_reach: 150000,
      influencer_reach_cpm: 3,
      influencer_impressions: 250000,
      influencer_impressions_cpm: 4,
      influencer_engagements: 15000,
      influencer_engagements_cpm: 6,
      attendance: 15000,
      attendance_cpm: 10,
      hospitality_guests: 50,
      hospitality_value_per_guest: 300,
      brand_popularity: 5,
      brand_csr: 6,
      brand_culture: 7,
      brand_loyalty: 6,
    },
  };

  const loadTestData = (example: 'football' | 'basketball') => {
    setAiDirty(true);
    const data = testDataExamples[example];
    setFormData((prev) => ({
      ...prev,
      merch_revenue: data.merch_revenue,
      matchday_revenue: data.matchday_revenue,
      // Only load player_sales if visible (organization setting + plan + permission)
      player_sales: canViewTransfer ? data.player_sales : [],
      player_sales_value: canViewTransfer
        ? data.player_sales.reduce((sum, sale) => sum + sale.sale_value, 0)
        : 0,
      tv_duration_min: data.tv_duration_min,
      tv_size_category: data.tv_size_category,
      tv_cpm: data.tv_cpm,
      tv_viewers: data.tv_viewers,
      webtv_views: data.webtv_views,
      webtv_cpm: data.webtv_cpm,
      pr_reach: data.pr_reach,
      pr_cpm: data.pr_cpm,
      digital_impressions: data.digital_impressions,
      digital_cpm: data.digital_cpm,
      social_followers: data.social_followers,
      social_followers_cpm: data.social_followers_cpm,
      social_impressions: data.social_impressions,
      social_impressions_cpm: data.social_impressions_cpm,
      social_engagements: data.social_engagements,
      social_cpm: data.social_cpm,
      influencer_reach: data.influencer_reach,
      influencer_reach_cpm: data.influencer_reach_cpm,
      influencer_impressions: data.influencer_impressions,
      influencer_impressions_cpm: data.influencer_impressions_cpm,
      influencer_engagements: data.influencer_engagements,
      influencer_engagements_cpm: data.influencer_engagements_cpm,
      attendance: data.attendance,
      attendance_cpm: data.attendance_cpm,
      hospitality_guests: data.hospitality_guests,
      hospitality_value_per_guest: data.hospitality_value_per_guest,
      brand_popularity: data.brand_popularity,
      brand_csr: data.brand_csr,
      brand_culture: data.brand_culture,
      brand_loyalty: data.brand_loyalty,
    }));
    // Sync the multi-line entries from the example's summary values (the
    // lines are the input mechanism for the multi-line media channels).
    setMediaEntries(seedMediaEntriesFromSummary({ ...formData, ...data }));
  };

  // Multiline example: multiple named entry lines per media channel.
  // Summary fields are synced from the lines via applyMediaEntries;
  // non-media fields reuse the football example values.
  const loadMultilineTestData = () => {
    const data = testDataExamples.football;
    setFormData((prev) => ({
      ...prev,
      merch_revenue: data.merch_revenue,
      matchday_revenue: data.matchday_revenue,
      player_sales: canViewTransfer ? data.player_sales : [],
      player_sales_value: canViewTransfer
        ? data.player_sales.reduce((sum, sale) => sum + sale.sale_value, 0)
        : 0,
      social_followers: data.social_followers,
      social_followers_cpm: data.social_followers_cpm,
      social_impressions: data.social_impressions,
      social_impressions_cpm: data.social_impressions_cpm,
      social_engagements: data.social_engagements,
      social_cpm: data.social_cpm,
      attendance: data.attendance,
      attendance_cpm: data.attendance_cpm,
      hospitality_guests: data.hospitality_guests,
      hospitality_value_per_guest: data.hospitality_value_per_guest,
      brand_popularity: data.brand_popularity,
      brand_csr: data.brand_csr,
      brand_culture: data.brand_culture,
      brand_loyalty: data.brand_loyalty,
    }));
    applyMediaEntries([
      {
        ...blankMediaEntry('tv'),
        name: 'National TV',
        volume: 400000,
        cpm: 25,
        duration_min: 45,
        size_category: 'L',
      },
      {
        ...blankMediaEntry('tv'),
        name: 'Regional TV',
        sort_order: 1,
        volume: 100000,
        cpm: 15,
        duration_min: 20,
        size_category: 'M',
      },
      { ...blankMediaEntry('webtv'), name: 'Club WebTV', volume: 150000, cpm: 8 },
      {
        ...blankMediaEntry('webtv'),
        name: 'Highlights Channel',
        sort_order: 1,
        volume: 50000,
        cpm: 6,
      },
      { ...blankMediaEntry('pr'), name: 'National Newspaper', volume: 600000, cpm: 6 },
      { ...blankMediaEntry('pr'), name: 'Local Media', sort_order: 1, volume: 400000, cpm: 4 },
      { ...blankMediaEntry('digital'), name: 'Club Website', volume: 1200000, cpm: 4 },
      {
        ...blankMediaEntry('digital'),
        name: 'Partner Sites',
        sort_order: 1,
        volume: 800000,
        cpm: 3,
      },
      {
        ...blankMediaEntry('influencer'),
        name: 'Star Influencer',
        volume: 500000,
        cpm: 5,
        impressions: 700000,
        impressions_cpm: 6,
        engagements: 50000,
        engagements_cpm: 10,
      },
      {
        ...blankMediaEntry('influencer'),
        name: 'Micro Influencer',
        sort_order: 1,
        volume: 300000,
        cpm: 4,
        impressions: 500000,
        impressions_cpm: 5,
        engagements: 30000,
        engagements_cpm: 8,
      },
    ]);
  };

  const resetForm = () => {
    setFormData({
      organization_id: formData.organization_id,
      valuation_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      updated_at: new Date().toISOString(),
      merch_revenue: 0,
      matchday_revenue: 0,
      player_sales_value: 0,
      player_sales: [],
      tv_duration_min: 0,
      tv_size_category: 'M',
      tv_cpm: 0,
      tv_viewers: 0,
      webtv_views: 0,
      webtv_cpm: 0,
      pr_reach: 0,
      pr_cpm: 0,
      digital_impressions: 0,
      digital_cpm: 0,
      social_followers: 0,
      social_followers_cpm: 0,
      social_impressions: 0,
      social_impressions_cpm: 0,
      social_engagements: 0,
      social_cpm: 0,
      influencer_reach: 0,
      influencer_reach_cpm: 0,
      influencer_impressions: 0,
      influencer_impressions_cpm: 0,
      influencer_engagements: 0,
      influencer_engagements_cpm: 0,
      attendance: 0,
      attendance_cpm: 0,
      hospitality_guests: 0,
      hospitality_value_per_guest: 0,
      brand_popularity: 5,
      brand_csr: 5,
      brand_culture: 5,
      brand_loyalty: 5,
    });
    setMediaEntries([]);
  };

  // Form data
  const [formData, setFormData] = useState<ValuationInput>(
    initialData || {
      organization_id: '',
      valuation_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      updated_at: new Date().toISOString(),
      merch_revenue: 0,
      matchday_revenue: 0,
      player_sales_value: 0,
      player_sales: [],
      tv_duration_min: 0,
      tv_size_category: 'M',
      tv_cpm: 0,
      tv_viewers: 0,
      webtv_views: 0,
      webtv_cpm: 0,
      pr_reach: 0,
      pr_cpm: 0,
      digital_impressions: 0,
      digital_cpm: 0,
      social_followers: 0,
      social_followers_cpm: 0,
      social_impressions: 0,
      social_impressions_cpm: 0,
      social_engagements: 0,
      social_cpm: 0,
      influencer_reach: 0,
      influencer_reach_cpm: 0,
      influencer_impressions: 0,
      influencer_impressions_cpm: 0,
      influencer_engagements: 0,
      influencer_engagements_cpm: 0,
      attendance: 0,
      attendance_cpm: 0,
      hospitality_guests: 0,
      hospitality_value_per_guest: 0,
      brand_popularity: 5,
      brand_csr: 5,
      brand_culture: 5,
      brand_loyalty: 5,
    }
  );

  // Multi-line media entries (plan Phase 5) — legacy summary values seed
  // line 1 per channel; the lines are the only input mechanism for the
  // multi-line channels (summary fields are synced from them below).
  const [mediaEntries, setMediaEntries] = useState<MediaEntryInput[]>(() =>
    initialData ? seedMediaEntriesFromSummary(initialData) : []
  );

  // EDIT mode: load the stored entry lines (replaces summary-seeding when
  // DB rows exist — legacy valuations without rows keep the seeded lines).
  const { data: loadedMediaEntries } = useMediaEntriesQuery(valuationId, organizationId, {
    enabled: mode === 'edit',
  });

  // Lines in channels the form does not edit as multi-line (merchandise/
  // matchday, e.g. created via MCP) — preserved untouched in the replace-all
  // save payload so saving the form never deletes them.
  const [otherChannelEntries, setOtherChannelEntries] = useState<MediaEntryInput[]>([]);

  // Apply loaded DB lines once per fetch result (render-adjust pattern —
  // setState during render is the lint-approved alternative to a setState
  // effect for derived state).
  const [appliedLoadedEntries, setAppliedLoadedEntries] = useState<MediaEntry[] | undefined>(
    undefined
  );
  if (loadedMediaEntries && loadedMediaEntries !== appliedLoadedEntries) {
    setAppliedLoadedEntries(loadedMediaEntries);
    if (loadedMediaEntries.length > 0) {
      const toInput = (e: MediaEntry): MediaEntryInput => ({
        channel: e.channel,
        name: e.name,
        sort_order: e.sort_order,
        volume: e.volume,
        cpm: e.cpm,
        duration_min: e.duration_min,
        size_category: e.size_category,
        impressions: e.impressions,
        impressions_cpm: e.impressions_cpm,
        engagements: e.engagements,
        engagements_cpm: e.engagements_cpm,
      });
      setMediaEntries(
        loadedMediaEntries
          .filter((e) => (FORM_MEDIA_CHANNELS as string[]).includes(e.channel))
          .map(toInput)
      );
      setOtherChannelEntries(
        loadedMediaEntries
          .filter((e) => !(FORM_MEDIA_CHANNELS as string[]).includes(e.channel))
          .map(toInput)
      );
    }
  }

  // Re-seed the entry lines when initialData changes (render-adjust pattern).
  // Skipped once DB lines are applied — they are the source of truth.
  const [seededFromInitialData, setSeededFromInitialData] = useState(initialData);
  if (initialData && initialData !== seededFromInitialData) {
    setSeededFromInitialData(initialData);
    if (!appliedLoadedEntries || appliedLoadedEntries.length === 0) {
      setMediaEntries(seedMediaEntriesFromSummary(initialData));
    }
  }

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  /**
   * Unsaved-changes flag for the AI form bridge: set by every user-facing
   * mutator (NOT by the initial data load), cleared after a successful save.
   * navigate_to refuses to leave the page while this is true.
   * (State, not a ref: the React Compiler forbids ref writes in functions
   * referenced during render; repeated same-value sets bail out, so this
   * costs at most one extra render per dirty/clean transition.)
   */
  const [aiDirty, setAiDirty] = useState(false);

  /**
   * Apply a new entries set + sync the summary fields into formData
   * (client-side version of the server sync-back, plan 5.3) so the live
   * calculator and the save payload stay correct per keystroke.
   */
  const applyMediaEntries = useCallback((next: MediaEntryInput[]) => {
    setAiDirty(true);
    setMediaEntries(next);
    setFormData((prev) => {
      const presentChannels = new Set(next.map((e) => e.channel));
      const zeros: Partial<ValuationInput> = {};
      for (const channel of FORM_MEDIA_CHANNELS) {
        if (!presentChannels.has(channel)) {
          Object.assign(zeros, MEDIA_CHANNEL_SUMMARY_ZERO[channel]);
        }
      }
      return { ...prev, ...zeros, ...buildSummaryFromEntries(next) };
    });
  }, []);

  const addMediaEntry = useCallback(
    (channel: FormMediaChannel) => {
      applyMediaEntries([...mediaEntries, blankMediaEntry(channel)]);
    },
    [mediaEntries, applyMediaEntries]
  );

  const updateMediaEntry = useCallback(
    (channel: FormMediaChannel, index: number, patch: Partial<MediaEntryInput>) => {
      let seen = -1;
      const next = mediaEntries.map((entry) => {
        if (entry.channel !== channel) return entry;
        seen += 1;
        return seen === index ? { ...entry, ...patch } : entry;
      });
      applyMediaEntries(next);
    },
    [mediaEntries, applyMediaEntries]
  );

  const deleteMediaEntry = useCallback(
    (channel: FormMediaChannel, index: number) => {
      let seen = -1;
      const next = mediaEntries.filter((entry) => {
        if (entry.channel !== channel) return true;
        seen += 1;
        return seen !== index;
      });
      applyMediaEntries(next);
    },
    [mediaEntries, applyMediaEntries]
  );

  // Confirm before deleting a line that has data (same pattern as player sales)
  const handleDeleteMediaEntryClick = useCallback(
    (channel: FormMediaChannel, index: number) => {
      const entry = mediaEntries.filter((e) => e.channel === channel)[index];
      if (entry && mediaEntryHasData(entry)) {
        setMediaEntryToDelete({ channel, index });
      } else {
        deleteMediaEntry(channel, index);
      }
    },
    [mediaEntries, deleteMediaEntry]
  );

  // Fetch platform metrics from database when opening in EDIT mode
  useEffect(() => {
    async function fetchPlatformMetrics() {
      if (mode !== 'edit' || !valuationId || !organizationId || !isSocialDeepDiveEnabled) {
        return;
      }

      try {
        const response = await fetchWithBypass(
          `/api/valuations/social-platforms?valuationId=${valuationId}&organizationId=${organizationId}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.platforms && data.platforms.length > 0) {
            // Convert array to Record<string, PlatformMetrics>
            const metricsMap = data.platforms.reduce(
              (acc: Record<string, PlatformMetrics>, platform: PlatformMetrics) => {
                acc[platform.platform] = platform;
                return acc;
              },
              {} as Record<string, PlatformMetrics>
            );
            setPlatformMetrics(metricsMap);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch platform metrics:', error);
      }
    }

    fetchPlatformMetrics();
  }, [mode, valuationId, organizationId, isSocialDeepDiveEnabled]);

  // Live calculation (memoized for performance)
  const calculation = useMemo(() => calculateCompleteValuation(formData), [formData]);

  const handleChange = useCallback(
    (field: keyof ValuationInput, value: string | number | Date | null) => {
      setAiDirty(true);
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const updatePlayerSale = (
    index: number,
    field: keyof PlayerSaleInput,
    value: string | number
  ) => {
    setAiDirty(true);
    const updatedSales = [...(formData.player_sales || [])];
    updatedSales[index] = { ...updatedSales[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      player_sales: updatedSales,
      player_sales_value: updatedSales.reduce((sum, sale) => sum + sale.sale_value, 0),
    }));
  };

  // ---- AI form bridge (assistant can FILL fields, never save) ----
  const aiFormBridge = useAiFormBridge();
  // Latest state/handlers for the registration closures (registered once on
  // mount — refs avoid stale closures over formData/mediaEntries)
  const aiFormRef = useRef({
    formData,
    mediaEntries,
    handleChange,
    updateMediaEntry,
    updatePlayerSale,
    getLabel,
    aiDirty,
  });
  useEffect(() => {
    aiFormRef.current = {
      formData,
      mediaEntries,
      handleChange,
      updateMediaEntry,
      updatePlayerSale,
      getLabel,
      aiDirty,
    };
  });
  useEffect(() => {
    /**
     * Flat numeric ValuationInput fields exposed to the assistant.
     * Player sales rows are exposed as player_sales[N].player_name /
     * .sale_value and written through updatePlayerSale, which recomputes
     * player_sales_value so the total never desyncs.
     */
    const FLAT_NUMBER_FIELDS: { field: keyof ValuationInput; label: string }[] = [
      { field: 'merch_revenue', label: 'Merchandise revenue' },
      { field: 'matchday_revenue', label: 'Matchday revenue' },
      { field: 'social_followers', label: 'Social Media → Social Followers' },
      { field: 'social_followers_cpm', label: 'Social Media → Followers CPM' },
      { field: 'social_impressions', label: 'Social Media → Social Impressions' },
      { field: 'social_impressions_cpm', label: 'Social Media → Impressions CPM' },
      { field: 'social_engagements', label: 'Social Media → Social Engagements' },
      { field: 'social_cpm', label: 'Social Media → Engagements CPM' },
      { field: 'attendance', label: 'Match & Hospitality → Attendance' },
      { field: 'attendance_cpm', label: 'Match & Hospitality → Attendance CPM' },
      { field: 'hospitality_guests', label: 'Match & Hospitality → VIP Guests' },
      { field: 'hospitality_value_per_guest', label: 'Match & Hospitality → Hospitality' },
      { field: 'brand_popularity', label: 'Brand Value Indicators → Popularity (1-10)' },
      { field: 'brand_csr', label: 'Brand Value Indicators → CSR (1-10)' },
      { field: 'brand_culture', label: 'Brand Value Indicators → Culture (1-10)' },
      { field: 'brand_loyalty', label: 'Brand Value Indicators → Loyalty (1-10)' },
    ];
    /** Media-entry columns exposed per line, by channel. */
    const ENTRY_FIELDS: Record<FormMediaChannel, (keyof MediaEntryInput)[]> = {
      tv: ['name', 'duration_min', 'size_category', 'volume', 'cpm'],
      webtv: ['name', 'volume', 'cpm'],
      pr: ['name', 'volume', 'cpm'],
      digital: ['name', 'volume', 'cpm'],
      influencer: [
        'name',
        'volume',
        'cpm',
        'impressions',
        'impressions_cpm',
        'engagements',
        'engagements_cpm',
      ],
    };
    /**
     * UI-accurate section titles and field labels (mirrors MediaEntrySection
     * config) so the assistant can match the user's words against what they
     * actually SEE — e.g. influencer "volume" is shown as "Reach".
     */
    const CHANNEL_TITLES: Record<FormMediaChannel, string> = {
      tv: 'TV',
      webtv: 'Web TV / Streaming',
      pr: 'PR / Earned Media',
      digital: 'Digital Display',
      influencer: 'Influencers',
    };
    const ENTRY_FIELD_LABELS: Record<FormMediaChannel, Partial<Record<string, string>>> = {
      tv: {
        name: 'Name',
        duration_min: 'Minutes',
        size_category: 'Size',
        volume: 'Viewers',
        cpm: 'CPM',
      },
      webtv: { name: 'Name', volume: 'Views', cpm: 'CPM' },
      pr: { name: 'Name', volume: 'Reach', cpm: 'CPM' },
      digital: { name: 'Name', volume: 'Impressions', cpm: 'CPM' },
      influencer: {
        name: 'Name',
        volume: 'Reach',
        cpm: 'Reach CPM',
        impressions: 'Impressions',
        impressions_cpm: 'Impr. CPM',
        engagements: 'Engagements',
        engagements_cpm: 'Eng. CPM',
      },
    };
    const ENTRY_NAME_PATTERN = /^(tv|webtv|pr|digital|influencer)\[(\d+)\]\.(\w+)$/;
    const PLAYER_SALE_PATTERN = /^player_sales\[(\d+)\]\.(player_name|sale_value)$/;

    return aiFormBridge.registerForm({
      formId: 'valuation',
      description:
        'Valuation form (financial, media, social, match & brand metrics). ' +
        'Media lines are named like tv[0].volume — the index is per channel. ' +
        'Player sale rows are named player_sales[0].player_name / .sale_value.',
      isDirty: () => aiFormRef.current.aiDirty,
      getFields: () => {
        const { formData: data, mediaEntries: entries, getLabel: label } = aiFormRef.current;
        const fields: AiFormFieldDescriptor[] = [
          {
            name: 'valuation_date',
            label: 'Valuation date',
            type: 'date',
            value: (data.valuation_date as string) ?? null,
          },
          ...FLAT_NUMBER_FIELDS.map(({ field, label: fallback }) => ({
            name: field as string,
            label:
              field === 'merch_revenue'
                ? label('merchandise_revenue')
                : field === 'matchday_revenue'
                  ? label('matchday_revenue')
                  : fallback,
            type: 'number' as const,
            value: typeof data[field] === 'number' ? (data[field] as number) : null,
          })),
        ];
        // Player sale rows (section title follows the org's label alias)
        (data.player_sales ?? []).forEach((sale, index) => {
          const rowLabel = sale.player_name?.trim() || `player ${index + 1}`;
          fields.push(
            {
              name: `player_sales[${index}].player_name`,
              label: `${label('transfer_value_title')} → ${rowLabel} — Player Name`,
              type: 'text',
              value: sale.player_name ?? null,
            },
            {
              name: `player_sales[${index}].sale_value`,
              label: `${label('transfer_value_title')} → ${rowLabel} — Sale Value`,
              type: 'number',
              value: typeof sale.sale_value === 'number' ? sale.sale_value : null,
            }
          );
        });
        // Per-channel index so names match updateMediaEntry's channel indexing
        const channelIndex: Record<string, number> = {};
        for (const entry of entries) {
          const channel = entry.channel as FormMediaChannel;
          const index = channelIndex[channel] ?? 0;
          channelIndex[channel] = index + 1;
          const lineLabel = entry.name?.trim() || `${channel} line ${index + 1}`;
          for (const key of ENTRY_FIELDS[channel] ?? []) {
            const value = entry[key];
            fields.push({
              name: `${channel}[${index}].${key}`,
              label: `${CHANNEL_TITLES[channel]} → ${lineLabel} — ${ENTRY_FIELD_LABELS[channel][key] ?? key}`,
              type: key === 'name' || key === 'size_category' ? 'text' : 'number',
              value: typeof value === 'number' || typeof value === 'string' ? value : null,
            });
          }
        }
        return fields;
      },
      setField: (name, value) => {
        const {
          handleChange: setFlat,
          updateMediaEntry: setEntry,
          mediaEntries: entries,
        } = aiFormRef.current;
        const entryMatch = name.match(ENTRY_NAME_PATTERN);
        if (entryMatch) {
          const channel = entryMatch[1] as FormMediaChannel;
          const index = Number(entryMatch[2]);
          const key = entryMatch[3] as keyof MediaEntryInput;
          if (!(ENTRY_FIELDS[channel] ?? []).includes(key)) return false;
          if (entries.filter((e) => e.channel === channel).length <= index) return false;
          if (key === 'size_category') {
            const size = String(value).toUpperCase();
            if (!['S', 'M', 'L', 'XL'].includes(size)) return false;
            setEntry(channel, index, { size_category: size as 'S' | 'M' | 'L' | 'XL' });
            return true;
          }
          setEntry(channel, index, {
            [key]: key === 'name' ? String(value) : Number(value),
          });
          return true;
        }
        const saleMatch = name.match(PLAYER_SALE_PATTERN);
        if (saleMatch) {
          const { formData: data, updatePlayerSale: setSale } = aiFormRef.current;
          const index = Number(saleMatch[1]);
          if ((data.player_sales ?? []).length <= index) return false;
          const key = saleMatch[2] as 'player_name' | 'sale_value';
          setSale(index, key, key === 'player_name' ? String(value) : Number(value));
          return true;
        }
        if (name === 'valuation_date') {
          setFlat('valuation_date', String(value));
          return true;
        }
        if (FLAT_NUMBER_FIELDS.some((f) => f.field === name)) {
          setFlat(name as keyof ValuationInput, Number(value));
          return true;
        }
        return false;
      },
    });
  }, [aiFormBridge]);

  const addPlayerSale = useCallback(() => {
    setAiDirty(true);
    const newSale: PlayerSaleInput = { player_name: '', sale_value: 0 };
    setFormData((prev) => {
      const updatedSales = [...(prev.player_sales || []), newSale];
      return {
        ...prev,
        player_sales: updatedSales,
        player_sales_value: updatedSales.reduce((sum, sale) => sum + sale.sale_value, 0),
      };
    });
  }, []);

  const handleDeletePlayerSaleClick = (index: number) => {
    const sale = formData.player_sales?.[index];

    // Check if sale has any data (player name or sale value)
    const hasData =
      sale &&
      ((sale.player_name && sale.player_name.trim() !== '') ||
        (sale.sale_value && sale.sale_value > 0));

    if (hasData) {
      // Show confirmation modal if there's data
      setPlayerSaleToDelete(index);
      setShowDeletePlayerSaleModal(true);
    } else {
      // Delete directly if empty
      removePlayerSale(index);
    }
  };

  const removePlayerSale = (index: number) => {
    setAiDirty(true);
    const updatedSales = (formData.player_sales || []).filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      player_sales: updatedSales,
      player_sales_value: updatedSales.reduce((sum, sale) => sum + sale.sale_value, 0),
    }));

    // Close modal and reset state
    setShowDeletePlayerSaleModal(false);
    setPlayerSaleToDelete(null);
  };

  // Check if user has filled in any data (excluding date)
  const hasFilledData = () => {
    // Check if any numeric fields are non-zero
    const hasNumericData =
      formData.merch_revenue !== 0 ||
      formData.matchday_revenue !== 0 ||
      formData.player_sales_value !== 0 ||
      formData.tv_duration_min !== 0 ||
      formData.tv_cpm !== 0 ||
      formData.tv_viewers !== 0 ||
      formData.webtv_views !== 0 ||
      formData.webtv_cpm !== 0 ||
      formData.pr_reach !== 0 ||
      formData.pr_cpm !== 0 ||
      formData.digital_impressions !== 0 ||
      formData.digital_cpm !== 0 ||
      formData.social_engagements !== 0 ||
      formData.social_cpm !== 0 ||
      (formData.influencer_reach ?? 0) !== 0 ||
      (formData.influencer_reach_cpm ?? 0) !== 0 ||
      (formData.influencer_impressions ?? 0) !== 0 ||
      (formData.influencer_impressions_cpm ?? 0) !== 0 ||
      (formData.influencer_engagements ?? 0) !== 0 ||
      (formData.influencer_engagements_cpm ?? 0) !== 0 ||
      formData.attendance !== 0 ||
      formData.attendance_cpm !== 0 ||
      formData.hospitality_guests !== 0 ||
      formData.hospitality_value_per_guest !== 0;

    // Check if brand values differ from default (5)
    const hasBrandData =
      formData.brand_popularity !== 5 ||
      formData.brand_csr !== 5 ||
      formData.brand_culture !== 5 ||
      formData.brand_loyalty !== 5;

    // Check if player sales have data
    const hasPlayerSales = (formData.player_sales || []).some(
      (sale) => (sale.player_name && sale.player_name.trim() !== '') || sale.sale_value !== 0
    );

    // Check if TV size category is not default (M)
    const hasTvSizeChange = formData.tv_size_category !== 'M';

    return hasNumericData || hasBrandData || hasPlayerSales || hasTvSizeChange;
  };

  const handleCancelOrDelete = () => {
    if (mode === 'create' && hasFilledData()) {
      // Block if in demo mode
      if (blockIfDemo()) return;
      // Show delete modal if data is filled
      setShowDeleteValuationModal(true);
    } else {
      // Just go back to dashboard if no data
      router.push('/dashboard');
    }
  };

  const handleConfirmDelete = () => {
    setShowDeleteValuationModal(false);
    router.push('/dashboard');
  };

  // Wrapper for onDelete prop that checks demo mode
  const handleDeleteClick = () => {
    // Block if in demo mode
    if (blockIfDemo()) return;
    // Call parent's onDelete
    if (onDelete) onDelete();
  };

  const handleSubmit = async (e: React.FormEvent, status: 'draft' | 'approved' = 'draft') => {
    e.preventDefault();

    // Block if in demo mode
    if (blockIfDemo()) return;

    setLoading(true);
    setSavingAction(status === 'approved' ? 'approve' : 'draft');
    setError('');

    try {
      // Normalize sort_order to the per-channel display order (array order)
      // so newly added lines keep their position after save + reload
      // (blankMediaEntry always starts at sort_order 0).
      const sortOrderCounters: Partial<Record<MediaEntryInput['channel'], number>> = {};
      const orderedEntries = mediaEntries.map((entry) => ({
        ...entry,
        sort_order: (sortOrderCounters[entry.channel] =
          (sortOrderCounters[entry.channel] ?? -1) + 1),
      }));
      await onSubmit(formData, calculation, status, [...orderedEntries, ...otherChannelEntries]);
      setAiDirty(false); // saved — the AI may navigate again
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t.errorOccurred;
      setError(errorMessage);
    } finally {
      setLoading(false);
      setSavingAction(null);
    }
  };

  const handleSaveDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (blockIfDemo()) return; // Block if in demo mode
    handleSubmit(e, 'draft');
  };

  const handleSaveAndApprove = (e: React.FormEvent) => {
    e.preventDefault();
    if (blockIfDemo()) return; // Block if in demo mode
    handleSubmit(e, 'approved');
  };

  // Check if user can approve (admin or approver)
  const canApprove = permissionsData?.canApprove ?? false;

  return (
    <>
      {/* Breadcrumb and Page Header */}
      <div>
        {/* Mobile: Fixed header with Back + Actions */}
        <div
          className={`fixed top-[56px] right-0 left-0 z-40 px-4 py-3 backdrop-blur-md sm:hidden ${
            darkMode ? 'bg-gray-900/70' : 'bg-blue-50/70'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <nav aria-label="Back" className="shrink-0">
              <SecondaryButton
                onClick={() => {
                  setNavigatingBack(true);
                  router.push('/valuations');
                }}
                loading={navigatingBack}
                className="flex items-center"
              >
                <ChevronLeftIcon aria-hidden="true" className="mr-1 -ml-1 size-5 shrink-0" />
                Back
              </SecondaryButton>
            </nav>
            <div className="flex shrink-0 gap-2 overflow-x-auto">
              {/* Cancel Button */}
              <SecondaryButton
                type="button"
                onClick={() => {
                  setNavigatingCancel(true);
                  router.push('/valuations');
                }}
                loading={navigatingCancel}
                className="rounded-full"
              >
                {common.cancel || 'Cancel'}
              </SecondaryButton>

              {/* Live Calculation Button */}
              <PrimaryButton
                type="button"
                onClick={() => setShowCalculationDrawer(!showCalculationDrawer)}
                className="rounded-full"
              >
                <CalculatorIcon className="h-5 w-5" strokeWidth={1.5} />
              </PrimaryButton>
            </div>
          </div>
        </div>

        {/* Desktop Breadcrumb */}
        <nav aria-label="Breadcrumb" className="hidden sm:flex sm:items-center sm:justify-between">
          <ol role="list" className="flex items-center space-x-4">
            <li>
              <div className="flex">
                <button
                  onClick={() => {
                    setNavigatingBack(true);
                    router.push('/valuations');
                  }}
                  disabled={navigatingBack}
                  className={`text-sm font-medium ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-50`}
                >
                  {navigatingBack ? 'Loading...' : 'Valuations'}
                </button>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon
                  aria-hidden="true"
                  className={`size-5 shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}
                />
                <span
                  aria-current="page"
                  className={`ml-4 text-sm font-medium ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {mode === 'create' ? t.newValuation : t.editValuation}
                </span>
                <StatusChip
                  status={mode === 'create' ? 'draft' : initialData?.status || 'draft'}
                  className="ml-8"
                />
              </div>
            </li>
          </ol>
        </nav>

        {/* Page Header with Actions */}
        <div className="mt-2 pt-10 sm:pt-0 md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2
              className={`text-2xl/7 font-bold sm:truncate sm:text-3xl sm:tracking-tight ${darkMode ? 'text-gray-50' : 'text-gray-900'}`}
            >
              {formData.valuation_date
                ? new Date(formData.valuation_date).toLocaleDateString('da-DK', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : clubName}
            </h2>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {mode === 'create' ? t.createDescription : t.editDescription}
            </p>
          </div>
          <div className="mt-4 hidden shrink-0 gap-3 sm:flex md:mt-0 md:ml-4">
            {/* Cancel Button (Desktop only - Mobile shows in sticky header) */}
            <SecondaryButton
              type="button"
              onClick={() => {
                setNavigatingCancel(true);
                router.push('/valuations');
              }}
              loading={navigatingCancel}
              className="rounded-full"
            >
              {common.cancel || 'Cancel'}
            </SecondaryButton>

            {/* Live Calculation Button (Desktop only - Mobile shows in sticky header) */}
            <PrimaryButton
              type="button"
              onClick={() => setShowCalculationDrawer(!showCalculationDrawer)}
              className="rounded-full"
            >
              <CalculatorIcon className="h-5 w-5" strokeWidth={1.5} />
            </PrimaryButton>

            {/* Test Data Dropdown - Show in create mode AND (demo mode OR localhost) */}
            {mode === 'create' && (isDemo || isLocalhost) && (
              <Menu as="div" className="relative inline-block text-left">
                <MenuButton
                  as={SecondaryButton}
                  className="inline-flex items-center gap-x-1.5 rounded-full"
                >
                  {t.loadExamples}
                  <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5" />
                </MenuButton>

                <MenuItems
                  transition
                  className={`absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y rounded-md shadow-lg ring-1 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in ${
                    darkMode
                      ? 'divide-gray-700 bg-gray-800 ring-white/5'
                      : 'divide-gray-100 bg-white ring-black/5'
                  }`}
                >
                  <div className="py-1">
                    <MenuItem>
                      <button
                        type="button"
                        onClick={() => loadTestData('football')}
                        className={`block w-full px-4 py-2 text-left text-sm ${
                          darkMode
                            ? 'text-gray-200 data-focus:bg-gray-700 data-focus:text-white'
                            : 'text-gray-700 data-focus:bg-gray-100 data-focus:text-gray-900'
                        } data-focus:outline-hidden`}
                      >
                        {t.footballExample}
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        type="button"
                        onClick={() => loadTestData('basketball')}
                        className={`block w-full px-4 py-2 text-left text-sm ${
                          darkMode
                            ? 'text-gray-200 data-focus:bg-gray-700 data-focus:text-white'
                            : 'text-gray-700 data-focus:bg-gray-100 data-focus:text-gray-900'
                        } data-focus:outline-hidden`}
                      >
                        {t.basketballExample}
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        type="button"
                        onClick={loadMultilineTestData}
                        className={`block w-full px-4 py-2 text-left text-sm ${
                          darkMode
                            ? 'text-gray-200 data-focus:bg-gray-700 data-focus:text-white'
                            : 'text-gray-700 data-focus:bg-gray-100 data-focus:text-gray-900'
                        } data-focus:outline-hidden`}
                      >
                        {t.multilineExample}
                      </button>
                    </MenuItem>
                  </div>
                  <div className="py-1">
                    <MenuItem>
                      <button
                        type="button"
                        onClick={resetForm}
                        className={`block w-full px-4 py-2 text-left text-sm ${
                          darkMode
                            ? 'text-red-400 data-focus:bg-red-500/10 data-focus:text-red-300'
                            : 'text-red-600 data-focus:bg-red-50 data-focus:text-red-700'
                        } data-focus:outline-hidden`}
                      >
                        {t.resetForm}
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            )}
          </div>
        </div>
      </div>

      {/* Grid Layout - Form and Live Calculation Side by Side */}
      <div
        className={`mt-8 pb-12 ${showCalculationDrawer && !isMobile ? 'grid grid-cols-1 gap-8 lg:grid-cols-3' : ''}`}
      >
        {/* Form Container - Takes 2/3 width on desktop when calculation is visible */}
        <div className={showCalculationDrawer && !isMobile ? 'lg:col-span-2' : ''}>
          <form onSubmit={handleSaveDraft} className="space-y-12">
            {/* Valuation Date Section */}
            <ValuationDateSection
              date={formData.valuation_date}
              onDateChange={(date) => handleChange('valuation_date', date)}
            />

            {/* Sales Value */}
            {canViewSales && (
              <SalesValueSection
                merchRevenue={formData.merch_revenue}
                matchdayRevenue={formData.matchday_revenue}
                onMerchRevenueChange={(value) => handleChange('merch_revenue', value)}
                onMatchdayRevenueChange={(value) => handleChange('matchday_revenue', value)}
                canEdit={canEditSales}
                numberFormat={localeSettings.numberFormat}
              />
            )}

            {/* Player Sales Value */}
            {canViewTransfer && (
              <PlayerSalesSection
                playerSales={formData.player_sales || []}
                onAddPlayerSale={addPlayerSale}
                onUpdatePlayerSale={updatePlayerSale}
                onDeletePlayerSale={handleDeletePlayerSaleClick}
                canEdit={canEditTransfer}
                numberFormat={localeSettings.numberFormat}
              />
            )}

            {/* Media Value Header */}
            {canViewMarketing && <MediaValueHeaderSection canEdit={canEditMarketing} />}

            {/* TV / Web TV / PR / Digital — multi-line entries (plan Phase 5) */}
            {canViewMarketing &&
              (['tv', 'webtv', 'pr', 'digital'] as const).map((channel) => (
                <MediaEntrySection
                  key={channel}
                  channel={channel}
                  entries={mediaEntries.filter((entry) => entry.channel === channel)}
                  onAddEntry={() => addMediaEntry(channel)}
                  onUpdateEntry={(index, patch) => updateMediaEntry(channel, index, patch)}
                  onDeleteEntry={(index) => handleDeleteMediaEntryClick(channel, index)}
                  canEdit={canEditMarketing}
                  numberFormat={localeSettings.numberFormat}
                />
              ))}

            {/* Social Media */}
            {canViewMarketing && (
              <SocialMediaSection
                socialFollowers={formData.social_followers}
                socialFollowersCpm={formData.social_followers_cpm}
                socialImpressions={formData.social_impressions}
                socialImpressionsCpm={formData.social_impressions_cpm}
                socialEngagements={formData.social_engagements}
                socialCpm={formData.social_cpm}
                onSocialFollowersChange={(value) => handleChange('social_followers', value)}
                onSocialFollowersCpmChange={(value) => handleChange('social_followers_cpm', value)}
                onSocialImpressionsChange={(value) => handleChange('social_impressions', value)}
                onSocialImpressionsCpmChange={(value) =>
                  handleChange('social_impressions_cpm', value)
                }
                onSocialEngagementsChange={(value) => handleChange('social_engagements', value)}
                onSocialCpmChange={(value) => handleChange('social_cpm', value)}
                canEdit={canEditMarketing}
                numberFormat={localeSettings.numberFormat}
                valuationId={valuationId || null}
                organizationId={organizationId}
                isPluginEnabled={isSocialDeepDiveEnabled}
                onPlatformBreakdownSave={(totals, metrics) => {
                  handleChange('social_followers', totals.followers);
                  handleChange('social_followers_cpm', totals.followers_cpm);
                  handleChange('social_impressions', totals.impressions);
                  handleChange('social_impressions_cpm', totals.impressions_cpm);
                  handleChange('social_engagements', totals.engagements);
                  handleChange('social_cpm', totals.engagements_cpm);
                  setPlatformMetrics(metrics); // Store platform metrics for persistence
                }}
                existingPlatformMetrics={platformMetrics}
              />
            )}

            {/* Influencers — multi-line entries (plan Phase 5) */}
            {canViewMarketing && (
              <MediaEntrySection
                channel="influencer"
                entries={mediaEntries.filter((entry) => entry.channel === 'influencer')}
                onAddEntry={() => addMediaEntry('influencer')}
                onUpdateEntry={(index, patch) => updateMediaEntry('influencer', index, patch)}
                onDeleteEntry={(index) => handleDeleteMediaEntryClick('influencer', index)}
                canEdit={canEditMarketing}
                numberFormat={localeSettings.numberFormat}
              />
            )}

            {/* Match & Hospitality */}
            {canViewMarketing && (
              <MatchHospitalitySection
                attendance={formData.attendance}
                attendanceCpm={formData.attendance_cpm}
                hospitalityGuests={formData.hospitality_guests}
                hospitalityValuePerGuest={formData.hospitality_value_per_guest}
                onAttendanceChange={(value) => handleChange('attendance', value)}
                onAttendanceCpmChange={(value) => handleChange('attendance_cpm', value)}
                onHospitalityGuestsChange={(value) => handleChange('hospitality_guests', value)}
                onHospitalityValuePerGuestChange={(value) =>
                  handleChange('hospitality_value_per_guest', value)
                }
                canEdit={canEditMarketing}
                numberFormat={localeSettings.numberFormat}
              />
            )}

            {/* Brand Value Indicators */}
            {canViewBrand && (
              <BrandValueSection
                popularity={formData.brand_popularity}
                csr={formData.brand_csr}
                culture={formData.brand_culture}
                loyalty={formData.brand_loyalty}
                onPopularityChange={(value) => handleChange('brand_popularity', value)}
                onCsrChange={(value) => handleChange('brand_csr', value)}
                onCultureChange={(value) => handleChange('brand_culture', value)}
                onLoyaltyChange={(value) => handleChange('brand_loyalty', value)}
                canEdit={canEditBrand}
                labels={{
                  popularity: t.popularity,
                  csr: t.csr,
                  culture: t.culture,
                  loyalty: t.fansLoyalty,
                }}
                helpTexts={{
                  popularity: t.popularityHelp,
                  csr: t.csrHelp,
                  culture: t.cultureHelp,
                  loyalty: t.fansLoyaltyHelp,
                }}
              />
            )}

            {error && (
              <div
                className={`mb-4 rounded p-3 text-sm ${
                  darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-900'
                }`}
              >
                {error}
              </div>
            )}

            {/* Action Buttons - Aligned with input fields (2/3 right side) */}
            <div className="grid grid-cols-1 gap-x-8 pt-6 md:grid-cols-3">
              {/* Empty left column to align with section titles */}
              <div></div>

              {/* Buttons in the middle/right columns (where inputs are) */}
              <div className="md:col-span-2">
                <div className="flex max-w-2xl flex-row items-center justify-between gap-3 sm:gap-4">
                  <div className="order-1">
                    {mode === 'edit' && onDelete && (
                      <DangerButton
                        type="button"
                        onClick={handleDeleteClick}
                        variant="solid"
                        className="w-auto"
                      >
                        {t.deleteValuation}
                      </DangerButton>
                    )}

                    {mode === 'create' &&
                      (hasFilledData() ? (
                        <DangerButton
                          type="button"
                          onClick={handleCancelOrDelete}
                          variant="solid"
                          className="w-auto"
                        >
                          {t.deleteValuation}
                        </DangerButton>
                      ) : (
                        <SecondaryButton
                          type="button"
                          onClick={handleCancelOrDelete}
                          className="w-auto"
                        >
                          {common.cancel || 'Cancel'}
                        </SecondaryButton>
                      ))}
                  </div>

                  <div className="order-2 flex flex-row gap-3 sm:gap-4">
                    <SecondaryButton type="submit" disabled={loading} className="w-auto">
                      {savingAction === 'draft' ? t.saving : 'Save Draft'}
                    </SecondaryButton>
                    {canApprove && (
                      <PrimaryButton
                        type="button"
                        onClick={handleSaveAndApprove}
                        disabled={loading}
                        className="w-auto"
                      >
                        {savingAction === 'approve' ? t.saving : 'Save and Approve'}
                      </PrimaryButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Live Calculation Panel - Right Column (1/3 width on desktop) */}
        {showCalculationDrawer && !isMobile && (
          <div className="lg:col-span-1">
            <div
              className={`sticky top-6 h-fit max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl p-3 backdrop-blur-xl md:p-6 ${
                darkMode
                  ? 'border border-white/10 bg-gray-900/70'
                  : 'border border-black/6 bg-white/70'
              }`}
            >
              <h3
                className={`mb-6 text-base font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
              >
                {t.liveCalculation}
              </h3>

              <div className="mb-6">
                <div
                  className={`mb-3 text-[15px] font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                >
                  {t.salesValueBreakdown}
                </div>

                {/* Player Sales */}
                {(formData.player_sales || []).length > 0 && (
                  <>
                    <div
                      className={`mt-2 mb-1 text-[11px] font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      {t.playerSales}
                    </div>
                    {formData.player_sales?.map((sale, index) => (
                      <div
                        key={index}
                        className={`mb-1 flex justify-between pl-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                      >
                        <span>{sale.player_name || `${t.player} ${index + 1}`}:</span>
                        <span>{formatCurrency(sale.sale_value, baseCurrency)}</span>
                      </div>
                    ))}
                  </>
                )}

                {/* Merchandise Revenue */}
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{getLabel('merchandise_revenue')}:</span>
                  <span>{formatCurrency(formData.merch_revenue, baseCurrency)}</span>
                </div>

                {/* Matchday Revenue */}
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{getLabel('matchday_revenue')}:</span>
                  <span>{formatCurrency(formData.matchday_revenue, baseCurrency)}</span>
                </div>

                {/* Total Sales Value */}
                <div
                  className={`mt-2 flex justify-between pt-2 text-base font-semibold ${
                    darkMode
                      ? 'border-t border-gray-700 text-gray-50'
                      : 'border-t border-gray-200 text-gray-900'
                  }`}
                >
                  <span>{t.totalSales}</span>
                  <span>{formatCurrency(calculation.salesValue, baseCurrency)}</span>
                </div>
              </div>

              <div className="mb-6">
                <div
                  className={`mb-3 text-[15px] font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                >
                  {t.mediaValueBreakdown}
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.tvShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.tv, baseCurrency)}</span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.webTvShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.webTv, baseCurrency)}</span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.prShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.pr, baseCurrency)}</span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.digitalShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.digital, baseCurrency)}</span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.socialShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.social.total, baseCurrency)}</span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.influencersShort}</span>
                  <span>
                    {formatCurrency(calculation.mediaValue.influencer.total, baseCurrency)}
                  </span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.attendanceShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.attendance, baseCurrency)}</span>
                </div>
                <div
                  className={`mb-1 flex justify-between text-base ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                >
                  <span>{t.hospitalityShort}</span>
                  <span>{formatCurrency(calculation.mediaValue.hospitality, baseCurrency)}</span>
                </div>
                <div
                  className={`mt-2 flex justify-between pt-2 text-base font-semibold ${
                    darkMode
                      ? 'border-t border-gray-700 text-gray-50'
                      : 'border-t border-gray-200 text-gray-900'
                  }`}
                >
                  <span>{t.totalMedia}</span>
                  <span>{formatCurrency(calculation.mediaValue.total, baseCurrency)}</span>
                </div>
              </div>

              <div className="mb-6">
                <div
                  className={`mb-2 text-[15px] font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                >
                  {t.qualitativeMultiplier}
                </div>
                <div
                  className={`text-sm font-semibold ${darkMode ? 'text-gray-50' : 'text-gray-900'}`}
                >
                  {calculation.qualitativeMultiplier.toFixed(2)}x
                </div>
              </div>

              <div
                className={`pt-4 ${darkMode ? 'border-t-2 border-gray-700' : 'border-t-2 border-gray-200'}`}
              >
                <div
                  className={`mb-1 text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {t.finalTotalValue}
                </div>
                <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-black'}`}>
                  {formatCurrency(calculation.finalTotal, baseCurrency)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Drawer - Only render on mobile to avoid conflicts */}
      {showCalculationDrawer && isMobile && (
        <SlideOverPanel
          isOpen={true}
          onClose={() => setShowCalculationDrawer(false)}
          title={t.liveCalculation}
        >
          <div className="mb-6">
            <div
              className={`mb-3 text-[15px] font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
            >
              {t.salesValueBreakdown}
            </div>

            {/* Player Sales */}
            {(formData.player_sales || []).length > 0 && (
              <>
                <div
                  className={`mt-2 mb-1 text-[11px] font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {t.playerSales}
                </div>
                {formData.player_sales?.map((sale, index) => (
                  <div
                    key={index}
                    className={`mb-1 flex justify-between pl-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
                  >
                    <span>{sale.player_name || `${t.player} ${index + 1}`}:</span>
                    <span>{formatCurrency(sale.sale_value, baseCurrency)}</span>
                  </div>
                ))}
              </>
            )}

            {/* Merchandise Revenue */}
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{getLabel('merchandise_revenue')}:</span>
              <span>{formatCurrency(formData.merch_revenue, baseCurrency)}</span>
            </div>

            {/* Matchday Revenue */}
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{getLabel('matchday_revenue')}:</span>
              <span>{formatCurrency(formData.matchday_revenue, baseCurrency)}</span>
            </div>

            {/* Total Sales Value */}
            <div
              className={`mt-2 flex justify-between pt-2 text-sm font-semibold ${
                darkMode
                  ? 'border-t border-gray-700 text-gray-50'
                  : 'border-t border-gray-200 text-gray-900'
              }`}
            >
              <span>{t.totalSales}</span>
              <span>{formatCurrency(calculation.salesValue, baseCurrency)}</span>
            </div>
          </div>

          <div className="mb-6">
            <div
              className={`mb-3 text-[15px] font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
            >
              {t.mediaValueBreakdown}
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.tvShort}</span>
              <span>{formatCurrency(calculation.mediaValue.tv, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.webTvShort}</span>
              <span>{formatCurrency(calculation.mediaValue.webTv, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.prShort}</span>
              <span>{formatCurrency(calculation.mediaValue.pr, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.digitalShort}</span>
              <span>{formatCurrency(calculation.mediaValue.digital, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.socialShort}</span>
              <span>{formatCurrency(calculation.mediaValue.social.total, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.influencersShort}</span>
              <span>{formatCurrency(calculation.mediaValue.influencer.total, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.attendanceShort}</span>
              <span>{formatCurrency(calculation.mediaValue.attendance, baseCurrency)}</span>
            </div>
            <div
              className={`mb-1 flex justify-between text-xs ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}
            >
              <span>{t.hospitalityShort}</span>
              <span>{formatCurrency(calculation.mediaValue.hospitality, baseCurrency)}</span>
            </div>
            <div
              className={`mt-2 flex justify-between pt-2 text-sm font-semibold ${
                darkMode
                  ? 'border-t border-gray-700 text-gray-50'
                  : 'border-t border-gray-200 text-gray-900'
              }`}
            >
              <span>{t.totalMedia}</span>
              <span>{formatCurrency(calculation.mediaValue.total, baseCurrency)}</span>
            </div>
          </div>

          <div className="mb-6">
            <div
              className={`mb-2 text-[15px] font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
            >
              {t.qualitativeMultiplier}
            </div>
            <div className={`text-sm font-semibold ${darkMode ? 'text-gray-50' : 'text-gray-900'}`}>
              {calculation.qualitativeMultiplier.toFixed(2)}x
            </div>
          </div>

          <div
            className={`pt-4 ${darkMode ? 'border-t-2 border-gray-700' : 'border-t-2 border-gray-200'}`}
          >
            <div
              className={`mb-1 text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
            >
              {t.finalTotalValue}
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-black'}`}>
              {formatCurrency(calculation.finalTotal, baseCurrency)}
            </div>
          </div>
        </SlideOverPanel>
      )}

      {/* Delete Player Sale Modal */}
      <ConfirmModal
        open={showDeletePlayerSaleModal}
        onClose={() => {
          setShowDeletePlayerSaleModal(false);
          setPlayerSaleToDelete(null);
        }}
        onConfirm={() => {
          if (playerSaleToDelete !== null) {
            removePlayerSale(playerSaleToDelete);
          }
        }}
        title="Delete Player Sale"
        message="Are you sure you want to delete this player sale entry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />

      {/* Delete Media Entry Modal */}
      <ConfirmModal
        open={mediaEntryToDelete !== null}
        onClose={() => setMediaEntryToDelete(null)}
        onConfirm={() => {
          if (mediaEntryToDelete !== null) {
            deleteMediaEntry(mediaEntryToDelete.channel, mediaEntryToDelete.index);
            setMediaEntryToDelete(null);
          }
        }}
        title="Delete Media Entry"
        message="Are you sure you want to delete this media entry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />

      {/* Delete Valuation Modal (for create mode with data) */}
      <ConfirmModal
        open={showDeleteValuationModal}
        onClose={() => setShowDeleteValuationModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Valuation"
        message="Are you sure you want to delete this valuation? All entered data will be lost. This action cannot be undone."
        confirmText="Delete Valuation"
        cancelText="Cancel"
        isDestructive={true}
      />

      {/* Demo Mode Upgrade Modal */}
      <UpgradeRequiredModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </>
  );
}

// Memoize component to prevent unnecessary re-renders
export default memo(ValuationForm);
